import { type CapacityStatus } from '@/components/chat/types/types';
import { type StructuredNoteSchema } from '@/components/tiptap/types';
import { type TabType } from '@/hooks/query/useQuerySessionWindows';
import type { GetIntegrationSchedulesResponse } from '@heidi/integrations/ehr/contracts';
import {
  type components,
  type EHRProvider,
  type operations,
  type TemplateCategory,
} from '@heidi/schema/ml-scribe';
import type { components as GlobalSchemaComponents } from '@heidi/schema/template-gallery';
import {
  type DictationSettingsRequestWithCursorAwareness,
  type DictationSettingsResponseWithCursorAwareness,
} from '@heidi/workspace/contracts';
import { type LucideIcon } from 'lucide-react';
import { type z } from 'zod';

// TimelineEventType was removed from the backend OpenAPI schema
// (timeline now uses discriminated unions). Define locally for consumers.
export enum TimelineEventType {
  note_created = 'note_created',
  note_regenerated = 'note_regenerated',
  template_changed = 'template_changed',
  voice_changed = 'voice_changed',
  scribe_changed = 'scribe_changed',
  note_reverted = 'note_reverted',
  edit = 'edit',
}

export type ArrayElementT<T> = T extends (infer U)[] ? U : T;

export type HardwareDeviceResponse = components['schemas']['HardwareDeviceResponse'];
export type LinkHardwareDeviceRequest =
  components['schemas']['LinkHardwareDeviceRequest'];
export type UnlinkHardwareDeviceResponse =
  components['schemas']['UnlinkHardwareDeviceResponse'];

// Replace with `components['schemas']['UpdateHardwareDeviceRequest']` after re-running `pnpm run schema:dev` (APP-4747).
export interface UpdateHardwareDeviceRequest {
  device_name?: string;
  firmware_version?: string;
}

export interface NavItem {
  title: string;
  href: string;
  icon: LucideIcon;
  color?: string;
  isChidren?: boolean;
  children?: NavItem[];
}

export enum AppType {
  Web = 'Web',
  Desktop = 'Desktop',
  AndroidApp = 'AndroidApp',
  iOSApp = 'iOSApp',
}

/**
 * The `creation_platform` discriminated union accepted by `POST /sessions/`.
 * Sourced from the generated backend schema (single source of truth) instead
 * of hand-written enums — built via `getSessionCreationPlatform`.
 */
export type CreationPlatform = NonNullable<CreateSessionRequest['creation_platform']>;

export enum CurrencySymbol {
  Dollar = '$',
  Pound = '£',
  Euro = '€',
  Rand = 'R',
  Yen = '¥',
  Yuan = 'CN¥',
  Franc = 'CHF',
  Rupiah = 'Rp',
  Ringgit = 'RM',
  HongKongDollar = 'HK$',
  SingaporeDollar = 'S$',
  AustralianDollar = 'A$',
  NewZealandDollar = 'NZ$',
  CanadianDollar = 'C$',
  IndianRupee = '₹',
  KoreanWon = '₩',
  PhilippinePeso = '₱',
  PakistaniRupee = '₨',
  PolishZloty = 'zł',
  SaudiRiyal = '﷼',
}

export enum TemplateCommunitySortBy {
  MOST_POPULAR = 'MOST_POPULAR',
  MOST_RECENT = 'MOST_RECENT',
  ALPHABETICAL = 'ALPHABETICAL',
}

export enum FilterSettings {
  ALL = 'ALL',
  INITIAL = 'INITIAL',
  UNDEFINED = 'UNDEFINED',
}

// `NotificationName` STOOD HERE and moved to `@heidi/workspace/contracts` with
// its only consumer, the notifications settings form. Deleted rather than
// re-exported: this barrel is imported eagerly across the app, so a re-export
// would buy a name with zero consumers at the price of a new eager edge into a
// package. The old path's name-intact rule governs the five settings modules
// the screens were served from, not an enum whose importer moved with it.

export const currencyCodeToSymbol: Record<string, CurrencySymbol> = {
  AUD: CurrencySymbol.Dollar,
  NZD: CurrencySymbol.Dollar,
  SGD: CurrencySymbol.Dollar,
  USD: CurrencySymbol.Dollar,
  GBP: CurrencySymbol.Pound,
  EUR: CurrencySymbol.Euro,
  ZAR: CurrencySymbol.Rand,
  HKD: CurrencySymbol.HongKongDollar,
  CAD: CurrencySymbol.CanadianDollar,
  CHF: CurrencySymbol.Franc,
  CNY: CurrencySymbol.Yuan,
  IDR: CurrencySymbol.Rupiah,
  JPY: CurrencySymbol.Yen,
  MYR: CurrencySymbol.Ringgit,
  INR: CurrencySymbol.IndianRupee,
  KRW: CurrencySymbol.KoreanWon,
  PHP: CurrencySymbol.PhilippinePeso,
  PKR: CurrencySymbol.PakistaniRupee,
  PLN: CurrencySymbol.PolishZloty,
  SAR: CurrencySymbol.SaudiRiyal,
};

export type UploadContextDocumentsMetaData = {
  name: string;
  date: string;
  additional_context: string;
  unstructured: boolean;
};

export type Maybe<T> = T | null | undefined;

export type AICommandResponseV2 = components['schemas']['AICommandResponseV2'];
export type GenerateTemplateContentUsingAIRequestV2 =
  components['schemas']['GenerateTemplateContentUsingAIRequestV2'];

export type CreateLetterFromDocumentRequest =
  components['schemas']['CreateLetterFromDocumentRequest'];
export type CreateSessionRequest =
  components['schemas']['domains__sessions__schemas__CreateSessionRequest'];
// Imported, not forwarded: the two names below type the template-section
// intersections that stay app-side.
import type {
  ConsultNoteWithSectionMappings,
  SectionMappingLink,
} from '@heidi/session/contracts';

export type ScheduleUploadView = components['schemas']['ScheduleUploadView'];

export type TranscriptResponse = components['schemas']['GenerateTranscriptResponseV2'];
export type SingleTranscriptResponse =
  components['schemas']['AudioSourceTranscriptResponse'];
export type SpecialtyTranscriptResponse =
  components['schemas']['SpecialtyTranscriptResponse'];
export type OnboardingScriptResponse = components['schemas']['OnboardingScriptResponse'];

export type DocumentResponse = components['schemas']['DocumentView'];
export type DictationEnabledSettingsRequest =
  components['schemas']['DictationEnabledSettingsRequest'];
export type DictationEnabledSettingsResponse =
  components['schemas']['DictationEnabledSettingsResponse'];
// FE overrides for dictation fields not yet in the generated schema
// (log_dictations_enabled, verbal_commands_enabled, verbal_commands_language,
// sound_enabled — see dictation-sound-cues.types.ts's chain).
export type DictationSettingsRequest = DictationSettingsRequestWithCursorAwareness;
export type DictationSettingsResponse = DictationSettingsResponseWithCursorAwareness;

export type ExtensionTask = components['schemas']['ExtensionTask'];

export type ModalViewAction = components['schemas']['ModalViewAction'];
export type ModalView = components['schemas']['ModalView'];
export type RecordModalViewRequest = components['schemas']['RecordModalViewRequest'];
export type UpdateAutoLinkIntentRequest =
  components['schemas']['UpdateAutoLinkIntentRequest'];
export type UpdateAutoExtractIntentRequest =
  components['schemas']['UpdateAutoExtractIntentRequest'];
export type UpdateDocumentRequest = components['schemas']['UpdateDocumentListRequest'];
export type GetDocumentResponse = components['schemas']['DocumentView'];
export type GenerateDocumentContentRequestV2 =
  components['schemas']['GenerateDocumentContentRequestV2'];
export type SimpleSessionViewV2 = components['schemas']['SimpleSessionViewV2'];
// TODO: Remove extension after backend PR #9446 merges and pnpm schema is re-run
export type ShareConsultNotesResponse =
  components['schemas']['ShareConsultNotesResponse'];
export type ShareDocumentThroughEmailRequestV2 =
  components['schemas']['ShareDocumentThroughEmailRequestV2'];
export type ShareDocumentResponseV2 = components['schemas']['ShareDocumentResponseV2'];
export type ShareConsultNoteRequest = components['schemas']['ShareConsultNoteRequest'];
export type deleteSessionsPayload =
  operations['batch_delete_sessions_api_v1_ml_scribe_sessions_batch_delete']['parameters']['query'];
export type DeleteSessionsResponse = components['schemas']['DeleteEmptySessionsResponse'];
export type NoteStructureOptionsResponse =
  components['schemas']['NoteStructureOptionsResponse'];
export type BuildCustomTemplateRequest =
  components['schemas']['BuildCustomTemplateRequest'];
// todo: fix after schema is deployed
export type Template = components['schemas']['TemplateResponseV2'];
export type DocumentAICommandsResponseV2 =
  components['schemas']['DocumentAICommandsResponseV2'];
export type ConsultNoteAICommandsResponseV2 =
  components['schemas']['ConsultNoteAICommandsResponseV2'];
export type GenerateConsultNoteRequest =
  components['schemas']['GenerateConsultNoteRequest'];
export type ErrorResponse = components['schemas']['ErrorResponse'];
export type IntegrationRequest = Omit<
  components['schemas']['database__mongo__base__IntegrationRequest'],
  'ehr_provider'
> & {
  ehr_provider: EHRProvider;
};

export type EHRProfileResponse = components['schemas']['EHRProfileResponse'];
export type CreateEHRProfileRequest = components['schemas']['CreateEHRProfileAPIRequest'];

/* EHR launch profile API */
export type UserEHRLaunchProfile = components['schemas']['UserEHRLaunchProfile'];

export type SyncNotesToEHRRequest = components['schemas']['SyncNotesToEHRAPIRequest'];

// Organization related types
export type InviteUsersToTeamResponse =
  components['schemas']['InviteUsersToTeamResponse'];

export type UserRole = components['schemas']['UserRole'];

export type BuildTemplateRequest = components['schemas']['BuildCustomTemplateRequest'];

export type PriceView = components['schemas']['PriceView'];

// This endpoint is intentionally absent from the development OpenAPI document,
// but remains consumed by the Evidence checkout client.
export type CreateEvidenceCheckoutRequest = {
  vendor: components['schemas']['PaymentVendor'];
  tier: components['schemas']['SubscriptionTier'];
  period: components['schemas']['shared__enums__enums__Period'];
  fe_endpoint: string;
  client_reference_id?: string | null;
  coupon_id?: string | null;
  credit_card_required: boolean | null;
  amplitude_device_id?: string | null;
  amplitude_session_id?: number | null;
};
export type EvidenceCheckoutResponse = {
  checkout_session_url?: string | null;
  subscription_id?: string | null;
  trial_end_date?: string | null;
};

export type DictationCleanupModesResponse =
  components['schemas']['DictationCleanupModeListReponse'];
export type DictationCleanupModesSingleResponse =
  components['schemas']['DictationCleanupModeResponse'];
export type ListRecordingsResponse = components['schemas']['GetRecordingsResponse'];

export type GenerateJWTResponse = components['schemas']['GenerateJWTResponse'];
export type LinkHeidiAccountResponse = components['schemas']['LinkHeidiAccountResponse'];
export type ValidateOpenAPIAccessResponse =
  components['schemas']['ValidateOpenAPIAccessResponse'];

export type UnlinkHeidiAccountResponse =
  components['schemas']['UnlinkHeidiAccountResponse'];

export type FeedbackRequest = components['schemas']['FeedbackFormRequest'];
export type FeedbackTags = components['schemas']['FeedbackRatingReason'][];
export type FeedbackV2Request = components['schemas']['SubmitFeedbackRequestV2'] & {
  /** FE-only augmentation — remove after `pnpm run schema:dev` picks up is_safety_issue (MK-1063). */
  feedback: components['schemas']['FeedbackV2'] & {
    is_safety_issue?: boolean;
  };
};

/** Request body for POST /sessions/{session_id}/safety-issue-ticket (MK-1062/MK-1063). */
export type SafetyIssueTicketRequest = {
  description: string;
};

/** Response body for POST /sessions/{session_id}/safety-issue-ticket (MK-1062/MK-1063). */
export type SafetyIssueTicketResponse = {
  ticket_reference: string;
};

export type LogoutEverywhereResponse = components['schemas']['LogoutEverywhereResponse'];
export type FeedbackEventTrackBaseProps = {
  feedbackCategory: TabType | undefined;
  noteCategory: TemplateCategory | TabType;
  userId: string;
  noteId: string;
  timestamp: string;
  documentName?: string;
};

export type FindReplaceContent = {
  [key: string]: string;
} | null;

export type UpdateDocumentsRequest = components['schemas']['UpdateDocumentListRequest'];
export type UpdateDocumentsResponse = components['schemas']['UpdateDocumentListResponse'];

/** File-private helper for the fill-form demo shapes below. Stayed behind when
 *  the template contracts moved (ARCFE-1012) — it is used by types that did
 *  not move, and the package had no use for it. */
type FillFormDemoFormField = GlobalSchemaComponents['schemas']['FormField'];

export type WordLibrary =
  | {
      [key: string]: string;
    }
  | null
  | undefined;

export type UserInmailResponse = components['schemas']['UserInmailResponse'];
export type GetInmailsResponse = components['schemas']['GetInmailsResponse'];
export type CreateUserInmailRequest = components['schemas']['CreateUserInmailRequest'];
export type GetUserNotificationPreferencesResponse =
  components['schemas']['GetUserNotificationPreferencesResponse'];

export type UpdateConsultNoteRequest = components['schemas']['UpdateConsultNoteRequest'];
export type UpdateConsultNoteResponse =
  components['schemas']['UpdateConsultNoteResponse'];

export type RoleModel = components['schemas']['RoleModel'];

export type SessionContextDocumentUpdateRequest =
  components['schemas']['UpdateSingleSessionContextDocument'];
export type UpdateSessionContextDocumentsRequest =
  components['schemas']['UpdateSessionContextDocumentsRequest'];
export type DeleteSessionContextDocumentsRequest =
  components['schemas']['DeleteSessionContextDocumentsRequest'];
export type DeleteSessionContextDocumentsResponse =
  components['schemas']['DeleteSessionContextDocumentsResponse'];

export type SessionClinicalEntity = components['schemas']['CodedClinicalEntity'];

export type GetEHRLaunchAccessTokenDetailsResponse =
  components['schemas']['GetEHRLaunchAccessTokenDetailsResponse'];
export type GetEHRLaunchPatientDetailsResponse =
  components['schemas']['GetEHRLaunchPatientDetailsResponse'];
export type GetEHRLaunchAuthorizationURLResponse =
  components['schemas']['GetEHRLaunchAuthorizationURLResponse'];

export type AbortStreamRequest = components['schemas']['StopDocumentGenerationRequest'];

export type GetAllFeatureFlagsResponse =
  components['schemas']['GetAllFeatureFlagsResponse'];

export type OutputLanguage = components['schemas']['LanguageCode'];
export type PermissionValue = components['schemas']['PermissionValue'];

export type LinkPatientSessionsRequest =
  components['schemas']['LinkPatientSessionsRequest'];

export type UnlinkPatientSessionsRequest =
  components['schemas']['UnlinkPatientSessionsRequest'];

export enum NotificationType {
  SESSION_EXPIRING = 'session_expiring',
  NEW_INVITATION = 'new_invitation',
  ROLE_CHANGE = 'role_change',
  SESSION_SHARED = 'session_shared',
  TASK_ASSIGNED = 'task_assigned',
  HCP_VERIFICATION_NEEDED = 'hcp_verification_needed',
  HCP_VERIFICATION_VERIFIED = 'hcp_verification_verified',
}

export type TranscriptLiveResponse =
  components['schemas']['GetSessionLiveTranscriptResponse'];

export type LiveTranscriptions = components['schemas']['LiveTranscriptResponse'];

export type RecordingChunk = components['schemas']['ChunkedRecordingWithTranscript'];

export type SingleChunk = components['schemas']['Chunk'];

export type CreateLiveTranscriptionResponse =
  components['schemas']['CreateLiveTranscriptionResponse'];

export type supportLanguagesResponse = components['schemas']['LanguageView'][];

// Re-export shim — the pick moved to `@heidi/patients/contracts`,
// where `extraction-fields-reviewed.ts` reads it behind the door. One wire
// contract, one declaration; a second alias here would be the same drift hazard
// as a duplicated cache key. NAMED, deliberately not `export *`.
export type { PatientContextResponse } from '@heidi/patients/contracts';

export type PatientContextLLMResponse =
  components['schemas']['PatientContextLLMResponse'];
export type SubmitClinicalCodeFeedbackRequest =
  components['schemas']['SubmitClinicalCodeFeedbackRequest'];

// E/M Level Determinator types — aliased from generated schema
export type EmPatientType = 'new_patient' | 'established_patient';
export type EmMdmElementDetail = components['schemas']['EmMdmElementResponse'];
export type EmMdmResult = components['schemas']['EmMdmResponse'];
export type EmConfirmed = components['schemas']['SessionEmLevelConfirmed'] | null;
export type EmSuggestedCode = components['schemas']['EmCodeDetail'];
export type EmAvailableCode = components['schemas']['EmCodeOption'];
export type EmConfirmRequest = {
  patient_type: EmPatientType;
  code: string;
  determination_method: string;
};

export type ExportPdfRequest = components['schemas']['ExportPdfRequest'] & {
  /**
   * When true the export renders the v2 letterhead grid layout instead of the
   * flat header/footer fields.
   * TODO: Remove extension after BE adds the field + pnpm schema is re-run.
   */
  apply_letterhead_layout?: boolean;
};

export type ExportDocxRequest = components['schemas']['ExportDocxRequest'] & {
  /**
   * Body font for the DOCX export. `font_family` is the Word font name to apply
   * (e.g. "Times New Roman"); `font_size` is in points (the picker number).
   * Both omitted → the backend stamps a 12pt Normal baseline.
   * TODO: Remove extension after BE adds the fields + pnpm schema is re-run.
   */
  font_family?: string | null;
  font_size?: number | null;
  /**
   * When true the export renders the v2 letterhead grid layout instead of the
   * flat header/footer fields.
   * TODO: Remove extension after BE adds the field + pnpm schema is re-run.
   */
  use_letterhead_editor?: boolean;
};

// Export Assets (header/footer logos and signatures)
export type ExportAssetUrlRequest = components['schemas']['ExportAssetUrlRequest'];
export type ExportAssetUrlResponse = components['schemas']['ExportAssetUrlResponse'];

/* Direct Integrations */
export type GetAvailableDirectIntegrationsResponse = string[];

export type GetIntegrationsScheduleType = GetIntegrationSchedulesResponse['schedule'];

export type SyncDocumentToIntegrationRequest = unknown;

export enum MeditechDocumentStatus {
  DRAFT = 'Draft',
  PENDING = 'Pending',
  SIGNED = 'Signed',
  DELETED = 'Deleted',
}
export type MeditechDocument = {
  document_reference: string;
  document_name: string;
  type: string;
  status: MeditechDocumentStatus;
  date_time: string;
};

// Call related types
export type CallSummaryView = components['schemas']['CallSummaryView'];
export type CallStatus = components['schemas']['CallStatus'];
export type AgentResponse = components['schemas']['AgentResponse'];
export type UpdateAgentRequest = components['schemas']['UpdateAgentRequest'];
export type Agent = components['schemas']['Agent'];
export type Message = components['schemas']['domainmodel__calls__models__Message'];
export type CallDirection = components['schemas']['CallDirection'];
export type CallContextValue = components['schemas']['CallContextValue'];
export type CallContextValues = components['schemas']['CallContextValues'];

// Inbox / Conversations
export type ConversationSummaryView = components['schemas']['ConversationSummaryView'];
export type LastMessageView = components['schemas']['LastMessageView'];
export type PatientMatchView = components['schemas']['PatientMatchView'];
export type AgentSummaryView = components['schemas']['AgentSummaryView'];
export type TranscriptTurnView = components['schemas']['TranscriptTurnView'];

// Patients (calls)
export type ReminderRecordView = components['schemas']['ReminderRecordView'];
export type CsvUploadRequest = components['schemas']['CsvUploadRequest'];

export type PersonalizationData = components['schemas']['PersonalizationData'];
export type PersonalizationInsight = components['schemas']['PersonalizationInsight'];
export type PersonalizationCategory = components['schemas']['PersonalizationCategory'];
export type DeletePersonalizationInsightRequest =
  components['schemas']['DeletePersonalizationInsightRequest'];
export type TogglePersonalizationInsightRequest =
  components['schemas']['TogglePersonalizationInsightRequest'];

/* Template Creation Record related START */

export type FillFormDemoRequest = {
  s3_object_key: string;
  form_fields: FillFormDemoFormField[];
  transcript: string;
  specialty_name?: string;
  country?: string;
  language?: string;
};

export type FillFormDemoFromUploadSource = 'audio' | 'document';

export type FillFormDemoFromUploadRequest = {
  source: FillFormDemoFromUploadSource;
  file: File;
  s3_object_key: string;
  form_fields: FillFormDemoFormField[];
  specialty_name?: string;
  country?: string;
  language?: string;
};

export type FillFormDemoResponse = {
  filled_pdf_base64: string;
  filled_pdf_mime_type: string;
};

export type FillXmlFormDemoRequest = {
  xml_source: string;
  form_fields: FillFormDemoFormField[];
  transcript: string;
  specialty_name?: string;
  country?: string;
  language?: string;
};

export type FillXmlFormDemoFromUploadRequest = {
  source: FillFormDemoFromUploadSource;
  file: File;
  xml_source: string;
  form_fields: FillFormDemoFormField[];
  specialty_name?: string;
  country?: string;
  language?: string;
};

export type FillXmlFormDemoResponse = {
  filled_xml: string;
  field_values: Record<string, string>;
};

// The JSON demo's wire types live behind the templates contracts door, beside
// the api fns that use them (MOD-1745). Re-exported here so the app spelling
// is unchanged for anything that still reaches for `@/types`.
export type {
  FillJsonFormDemoFromUploadRequest,
  FillJsonFormDemoRequest,
  FillJsonFormDemoResponse,
} from '@heidi/templates/contracts';

export type CreateVersionControlFromExampleRequest =
  components['schemas']['CreateVersionControlFromExampleRequest'];

/* Template Creation Record related END */

/* EHR Letter Templates */
export type ListLetterTemplatesResponse =
  components['schemas']['ListLetterTemplatesResponse'];
export type DocumentTemplate = components['schemas']['DocumentTemplate'];
export type LetterAttachmentListResponse =
  components['schemas']['LetterAttachmentListResponse'];
export type LetterAttachmentDownloadUrlResponse =
  components['schemas']['LetterAttachmentDownloadUrlResponse'];
export type LetterAttachmentResponse = components['schemas']['LetterAttachmentResponse'];
export type LetterContentSnapshotTimelineEventResponse =
  components['schemas']['LetterContentSnapshotTimelineEventResponse'];
export type LetterDeliveryDetails = components['schemas']['LetterDeliveryDetails'];
export type LetterElectronicallySignedSignature =
  components['schemas']['LetterElectronicallySignedSignature'];
export type LetterHeadResponse = components['schemas']['LetterheadPreviewResponse'];
export type LetterTimelineEventListResponse =
  components['schemas']['LetterTimelineEventListResponse'];
export type LetterTimelineEventRecordResponse =
  components['schemas']['LetterTimelineEventRecordResponse'];
export type LetterResponse = components['schemas']['LetterResponse'];
export type LetterSignatureUser = components['schemas']['LetterSignatureUser'];
export type ListLettersResponse = components['schemas']['LetterListResponse'];
export type SendLetterRequest = components['schemas']['SendLetterRequest'];
export type SendLetterResponse = components['schemas']['SendLetterResponse'];
export type UploadLetterAttachmentRequest =
  components['schemas']['Body_upload_letter_attachment_api_v2_ml_scribe_sessions__session_id__letters__letter_id__attachments_post'];
export type SignLetterRequest = components['schemas']['SignLetterRequest'];
export type UpdateLetterRequest = components['schemas']['UpdateLetterRequest'];
export type LetterSealedMessageDeliveryRecipient =
  components['schemas']['LetterSealedMessageDeliveryRecipient'];
export type LetterEmailRecipient = components['schemas']['LetterEmailRecipient'];
export type LetterDirectoryRecipient = components['schemas']['LetterDirectoryRecipient'];
export type LetterFaxRecipient = components['schemas']['LetterFaxRecipient'];
export type LetterRecipient =
  | LetterSealedMessageDeliveryRecipient
  | LetterEmailRecipient
  | LetterDirectoryRecipient
  | LetterFaxRecipient;
export type RecipientSearchResponse = components['schemas']['RecipientSearchResponse'];
export type DirectoryRecipientSearchResultResponse =
  components['schemas']['DirectoryRecipientSearchResultResponse'];
export type SealedMessageDeliveryRecipientSearchResultResponse =
  components['schemas']['SealedMessageDeliveryRecipientSearchResultResponse'];
export type LetterSendTimelineEventResponse =
  components['schemas']['LetterSendTimelineEventResponse'];

export type ConsultNoteResponseV2 = components['schemas']['ConsultNoteResponseV2'];
export type GetConsultNotesResponse = components['schemas']['GetConsultNotesResponse'];
export type GetConsultNoteTimelineResponse =
  components['schemas']['GetConsultNoteTimelineResponse'];
export type NoteCreatedEvent = components['schemas']['NoteCreatedEvent'];
export type NoteRegeneratedEvent = components['schemas']['NoteRegeneratedEvent'];
export type TemplateChangedEvent = components['schemas']['TemplateChangedEvent'];
export type VoiceChangedEvent = components['schemas']['VoiceChangedEvent'];
export type ScribeChangedEvent = components['schemas']['ScribeChangedEvent'];
export type NoteRevertedEvent = components['schemas']['NoteRevertedEvent'];
export type EditEvent = components['schemas']['EditEvent'];
export type TimelineEvent =
  | NoteCreatedEvent
  | NoteRegeneratedEvent
  | TemplateChangedEvent
  | VoiceChangedEvent
  | ScribeChangedEvent
  | NoteRevertedEvent
  | EditEvent;

export type UpdateJSONConsultNoteSectionLinkRequest =
  components['schemas']['UpdateConsultNoteSectionLinkRequest'];
export type UpdateJSONConsultNoteSectionLinkResponse = Omit<
  components['schemas']['UpdateConsultNoteSectionLinkResponse'],
  'consult_note'
> & {
  consult_note: ConsultNoteWithSectionMappings<
    components['schemas']['ConsultNoteResponseV2']
  >;
};

export type AvailableEHRSection = components['schemas']['AvailableSection'];
export type GetAvailableEHRSectionsResponse =
  components['schemas']['AvailableSectionsResponse'];

/**
 * INT-4603 template-section-mapping types. Hand-defined to match BE PR #10109
 * (apis/v2/integrations/template_section_mappings/schemas.py); will be replaced
 * by the generated OpenAPI shapes once the schema is regenerated.
 */
export type TemplateSectionLink = SectionMappingLink;

export type ConsultNoteLinkedSectionContent = {
  content: string;
  linked_ehr_section_id: string;
};

/* Identity Broker related types */
export type DetermineLoginResponse = components['schemas']['DetermineLoginResponseDTO'];

/**
 * Response of `POST /api/v1/identity-broker/assign-apple-migration-ticket`
 * (oscerai/ml-scribe#11078 / MUL-2093). Hand-written until the endpoint ships
 * in the generated OpenAPI spec; `ticket_id` is the UUIDv7 to hand to Auth0
 * and is non-null only for the two ticket statuses.
 */
export interface AssignAppleMigrationTicketResponse {
  status:
    | 'ticket_created'
    | 'ticket_refreshed'
    | 'already_migrated'
    | 'migration_in_progress'
    | 'migration_failed'
    | 'no_migration_needed';
  ticket_id: string | null;
}

// export const JSONTemplateSchema = z
//   .object({
//     globalInstructions: z.string().default(''),
//     sections: z
//       .array(
//         z.object({
//           heading: z.string(),
//           showHeading: z.boolean(),
//           template: z.string().nullish(),
//           content: z.string().nullish(),
//           linked_ehr_section_id: z.string().nullish(),
//           sectionInstructions: z.string().nullable(),
//         })
//       )
//       .nullish(),
//   })
//   .default({
//     globalInstructions: '',
//     sections: [],
//   });

// export type TemplateJSON = z.infer<typeof JSONTemplateSchema>;

// export type TemplateLinkedSectionContent = NonNullable<
//   z.infer<typeof JSONTemplateSchema>['sections']
// >[number];

export type StructuredNote = z.infer<typeof StructuredNoteSchema>;
export type ConvertPlainTextNoteToSectionalNoteResponse =
  components['schemas']['ConvertPlainTextNoteToSectionalNoteResponse'];

export type RefreshAppointmentTypesResponse =
  components['schemas']['RefreshAppointmentTypesResponse'];
export type RefreshCliniciansResponse =
  components['schemas']['RefreshCliniciansResponse'];
export type RefreshClinicsResponse = components['schemas']['RefreshClinicsResponse'];

// Ehr Launch latest note
export type GetEHRLatestNoteResponse = components['schemas']['GetEHRLatestNoteResponse'];
export type GetEhrLaunchPastNotesWithCommonlyUsedNotesResponse =
  components['schemas']['GetPastEHRNotesWithCommonlyUsedResponse'];

export type IntegrationOnboardingStepExecutionRequest =
  components['schemas']['IntegrationOnboardingStepExecutionRequest'];
export type IntegrationOnboardingStepExecutionResponse =
  components['schemas']['IntegrationOnboardingStepExecutionResponse'];
export type PendingIntegrationOnboardingExecutionStep =
  components['schemas']['PendingIntegrationOnboardingExecutionStep'];
export type OnboardingSessionStatus = components['schemas']['OnboardingSessionStatus'];
export type MKCustomTemplateRequestResponse =
  components['schemas']['MKCustomTemplateRequestResponse'];
export type MKPaginatedTemplateRequestResponse =
  components['schemas']['PaginatedMKCustomTemplateRequestResponse'];
export type GetMobileRequest = components['schemas']['GetMobileRequest'];

export type SeenModals = {
  [key: string]: ModalView;
};

export type ChatResponseBase = components['schemas']['ChatResponse'];
export type ChatResponse = ChatResponseBase & {
  document_size_bytes?: number;
  capacity_status?: CapacityStatus;
};
export type ChatMessageResponse = components['schemas']['ChatMessageResponse'];
export type ChatListItem = components['schemas']['ChatListItem'];
export type ChatMessageMetadataResponse =
  components['schemas']['ChatMessageMetadataResponse'];
export type CreateChatRequest = components['schemas']['CreateChatRequest'];
export type CreateChatResponse = components['schemas']['CreateChatResponse'];
export type StoreChatsFromLocalStorageRequest =
  components['schemas']['StoreChatsFromLocalStorageRequest'];
export type StoreChatsFromLocalStorageResponse =
  components['schemas']['StoreChatsFromLocalStorageResponse'];
export type LocalStorageChatMessageRequest =
  components['schemas']['LocalStorageChatMessageRequest'];
export type LocalStorageChatRequest = components['schemas']['LocalStorageChatRequest'];
export type EvidenceBillingInfoResponseView =
  components['schemas']['domains__billing__api__billing_schema__EvidenceBillingInfoResponseView'];
// Note: This is the evidence billing type returned by the /user endpoint.
// For the /billings endpoint, use components['schemas']['apis__v2__billing__billing_schema__EvidenceBillingInfoResponseView']

// Note: this is a hack for now - ideally backend should be exporting these enums
// And also this is just being used to make sure the user currency and country

export enum Currency {
  USD = 'usd',
  AUD = 'aud',
  CAD = 'cad',
  CHF = 'chf',
  CNY = 'cny',
  EUR = 'eur',
  GBP = 'gbp',
  HKD = 'hkd',
  IDR = 'idr',
  JPY = 'jpy',
  MYR = 'myr',
  NZD = 'nzd',
  SGD = 'sgd',
  ZAR = 'zar',
  INR = 'inr',
  KRW = 'krw',
  PHP = 'php',
  PKR = 'pkr',
  PLN = 'pln',
  SAR = 'sar',
}

export enum Country {
  AUSTRALIA = 'Australia',
  AFGHANISTAN = 'Afghanistan',
  ALBANIA = 'Albania',
  ALGERIA = 'Algeria',
  AMERICAN_SAMOA = 'American Samoa',
  ANDORRA = 'Andorra',
  ANGOLA = 'Angola',
  ANGUILLA = 'Anguilla',
  ANTARCTICA = 'Antarctica',
  ANTIGUA_AND_BARBUDA = 'Antigua and Barbuda',
  ARGENTINA = 'Argentina',
  ARMENIA = 'Armenia',
  ARUBA = 'Aruba',
  AUSTRIA = 'Austria',
  AZERBAIJAN = 'Azerbaijan',
  BAHAMAS = 'Bahamas',
  BAHRAIN = 'Bahrain',
  BANGLADESH = 'Bangladesh',
  BARBADOS = 'Barbados',
  BELARUS = 'Belarus',
  BELGIUM = 'Belgium',
  BELIZE = 'Belize',
  BENIN = 'Benin',
  BERMUDA = 'Bermuda',
  BHUTAN = 'Bhutan',
  BOLIVIA = 'Bolivia',
  BOSNIA_AND_HERZEGOVINA = 'Bosnia and Herzegovina',
  BOTSWANA = 'Botswana',
  BRAZIL = 'Brazil',
  BRITISH_INDIAN_OCEAN_TERRITORY = 'British Indian Ocean Territory',
  BRUNEI_DARUSSALAM = 'Brunei Darussalam',
  BULGARIA = 'Bulgaria',
  BURKINA_FASO = 'Burkina Faso',
  BURUNDI = 'Burundi',
  CAMBODIA = 'Cambodia',
  CAMEROON = 'Cameroon',
  CANADA = 'Canada',
  CAPE_VERDE = 'Cape Verde',
  CAYMAN_ISLANDS = 'Cayman Islands',
  CENTRAL_AFRICAN_REPUBLIC = 'Central African Republic',
  CHAD = 'Chad',
  CHILE = 'Chile',
  CHINA = 'China',
  CHRISTMAS_ISLAND = 'Christmas Island',
  COCOS_KEELING_ISLANDS = 'Cocos (Keeling) Islands',
  COLOMBIA = 'Colombia',
  COMOROS = 'Comoros',
  CONGO_DEMOCRATIC_REPUBLIC = 'Congo, Democratic Republic of the',
  CONGO_REPUBLIC = 'Congo, Republic of the',
  COOK_ISLANDS = 'Cook Islands',
  COSTA_RICA = 'Costa Rica',
  COTE_DIVOIRE = "Cote d'Ivoire",
  CROATIA = 'Croatia',
  CUBA = 'Cuba',
  CURACAO = 'Curacao',
  CYPRUS = 'Cyprus',
  CZECH_REPUBLIC = 'Czech Republic',
  DENMARK = 'Denmark',
  DJIBOUTI = 'Djibouti',
  DOMINICA = 'Dominica',
  DOMINICAN_REPUBLIC = 'Dominican Republic',
  ECUADOR = 'Ecuador',
  EGYPT = 'Egypt',
  EL_SALVADOR = 'El Salvador',
  EQUATORIAL_GUINEA = 'Equatorial Guinea',
  ERITREA = 'Eritrea',
  ESTONIA = 'Estonia',
  ETHIOPIA = 'Ethiopia',
  FALKLAND_ISLANDS = 'Falkland Islands (Malvinas)',
  FAROE_ISLANDS = 'Faroe Islands',
  FIJI = 'Fiji',
  FINLAND = 'Finland',
  FRANCE = 'France',
  FRENCH_GUIANA = 'French Guiana',
  FRENCH_SOUTHERN_TERRITORIES = 'French Southern Territories',
  GABON = 'Gabon',
  GAMBIA = 'Gambia',
  GEORGIA = 'Georgia',
  GERMANY = 'Germany',
  GHANA = 'Ghana',
  GIBRALTAR = 'Gibraltar',
  GREECE = 'Greece',
  GREENLAND = 'Greenland',
  GRENADA = 'Grenada',
  GUADELOUPE = 'Guadeloupe',
  GUAM = 'Guam',
  GUATEMALA = 'Guatemala',
  GUERNSEY = 'Guernsey',
  GUINEA = 'Guinea',
  GUINEA_BISSAU = 'Guinea-Bissau',
  GUYANA = 'Guyana',
  HAITI = 'Haiti',
  HOLY_SEE = 'Holy See (Vatican City State)',
  HONDURAS = 'Honduras',
  HONG_KONG = 'Hong Kong',
  HUNGARY = 'Hungary',
  ICELAND = 'Iceland',
  INDIA = 'India',
  INDONESIA = 'Indonesia',
  IRAN = 'Iran, Islamic Republic of',
  IRAQ = 'Iraq',
  IRELAND = 'Ireland',
  ISLE_OF_MAN = 'Isle of Man',
  ISRAEL = 'Israel',
  ITALY = 'Italy',
  JAMAICA = 'Jamaica',
  JAPAN = 'Japan',
  JERSEY = 'Jersey',
  JORDAN = 'Jordan',
  KAZAKHSTAN = 'Kazakhstan',
  KENYA = 'Kenya',
  KIRIBATI = 'Kiribati',
  KOREA_DEMOCRATIC_PEOPLES_REPUBLIC = "Korea, Democratic People's Republic of",
  KOREA_REPUBLIC = 'Korea, Republic of',
  KUWAIT = 'Kuwait',
  KYRGYZSTAN = 'Kyrgyzstan',
  LAO_PEOPLES_DEMOCRATIC_REPUBLIC = "Lao People's Democratic Republic",
  LATVIA = 'Latvia',
  LEBANON = 'Lebanon',
  LESOTHO = 'Lesotho',
  LIBERIA = 'Liberia',
  LIBYA = 'Libya',
  LIECHTENSTEIN = 'Liechtenstein',
  LITHUANIA = 'Lithuania',
  LUXEMBOURG = 'Luxembourg',
  MACAO = 'Macao',
  MACEDONIA = 'Macedonia, the Former Yugoslav Republic of',
  MADAGASCAR = 'Madagascar',
  MALAWI = 'Malawi',
  MALAYSIA = 'Malaysia',
  MALDIVES = 'Maldives',
  MALI = 'Mali',
  MALTA = 'Malta',
  MARSHALL_ISLANDS = 'Marshall Islands',
  MARTINIQUE = 'Martinique',
  MAURITANIA = 'Mauritania',
  MAURITIUS = 'Mauritius',
  MAYOTTE = 'Mayotte',
  MEXICO = 'Mexico',
  MICRONESIA = 'Micronesia, Federated States of',
  MOLDOVA = 'Moldova, Republic of',
  MONACO = 'Monaco',
  MONGOLIA = 'Mongolia',
  MONTENEGRO = 'Montenegro',
  MONTSERRAT = 'Montserrat',
  MOROCCO = 'Morocco',
  MOZAMBIQUE = 'Mozambique',
  MYANMAR = 'Myanmar',
  NAMIBIA = 'Namibia',
  NAURU = 'Nauru',
  NEPAL = 'Nepal',
  NETHERLANDS = 'Netherlands',
  NEW_CALEDONIA = 'New Caledonia',
  NEW_ZEALAND = 'New Zealand',
  NICARAGUA = 'Nicaragua',
  NIGER = 'Niger',
  NIGERIA = 'Nigeria',
  NIUE = 'Niue',
  NORFOLK_ISLAND = 'Norfolk Island',
  NORTHERN_MARIANA_ISLANDS = 'Northern Mariana Islands',
  NORWAY = 'Norway',
  OMAN = 'Oman',
  PAKISTAN = 'Pakistan',
  PALAU = 'Palau',
  PALESTINE = 'Palestine, State of',
  PANAMA = 'Panama',
  PAPUA_NEW_GUINEA = 'Papua New Guinea',
  PARAGUAY = 'Paraguay',
  PERU = 'Peru',
  PHILIPPINES = 'Philippines',
  PITCAIRN = 'Pitcairn',
  POLAND = 'Poland',
  PORTUGAL = 'Portugal',
  PUERTO_RICO = 'Puerto Rico',
  QATAR = 'Qatar',
  REUNION = 'Reunion',
  ROMANIA = 'Romania',
  RUSSIAN_FEDERATION = 'Russian Federation',
  RWANDA = 'Rwanda',
  SAINT_BARTHELEMY = 'Saint Barthelemy',
  SAINT_HELENA = 'Saint Helena',
  SAINT_KITTS_AND_NEVIS = 'Saint Kitts and Nevis',
  SAINT_LUCIA = 'Saint Lucia',
  SAINT_MARTIN = 'Saint Martin (French part)',
  SAINT_PIERRE_AND_MIQUELON = 'Saint Pierre and Miquelon',
  SAINT_VINCENT_AND_GRENADINES = 'Saint Vincent and the Grenadines',
  SAMOA = 'Samoa',
  SAN_MARINO = 'San Marino',
  SAO_TOME_AND_PRINCIPE = 'Sao Tome and Principe',
  SAUDI_ARABIA = 'Saudi Arabia',
  SENEGAL = 'Senegal',
  SERBIA = 'Serbia',
  SEYCHELLES = 'Seychelles',
  SIERRA_LEONE = 'Sierra Leone',
  SINGAPORE = 'Singapore',
  SINT_MAARTEN = 'Sint Maarten (Dutch part)',
  SLOVAKIA = 'Slovakia',
  SLOVENIA = 'Slovenia',
  SOLOMON_ISLANDS = 'Solomon Islands',
  SOMALIA = 'Somalia',
  SOUTH_AFRICA = 'South Africa',
  SOUTH_GEORGIA_AND_SANDWICH_ISLANDS = 'South Georgia and the South Sandwich Islands',
  SOUTH_SUDAN = 'South Sudan',
  SPAIN = 'Spain',
  SRI_LANKA = 'Sri Lanka',
  SUDAN = 'Sudan',
  SURINAME = 'Suriname',
  SVALBARD_AND_JAN_MAYEN = 'Svalbard and Jan Mayen',
  SWAZILAND = 'Swaziland',
  SWEDEN = 'Sweden',
  SWITZERLAND = 'Switzerland',
  SYRIAN_ARAB_REPUBLIC = 'Syrian Arab Republic',
  TAIWAN = 'Taiwan, Province of China',
  TAJIKISTAN = 'Tajikistan',
  TANZANIA = 'Tanzania, United Republic of',
  THAILAND = 'Thailand',
  TIMOR_LESTE = 'Timor-Leste',
  TOGO = 'Togo',
  TOKELAU = 'Tokelau',
  TONGA = 'Tonga',
  TRINIDAD_AND_TOBAGO = 'Trinidad and Tobago',
  TUNISIA = 'Tunisia',
  TURKEY = 'Turkey',
  TURKMENISTAN = 'Turkmenistan',
  TURKS_AND_CAICOS_ISLANDS = 'Turks and Caicos Islands',
  TUVALU = 'Tuvalu',
  UGANDA = 'Uganda',
  UKRAINE = 'Ukraine',
  UNITED_ARAB_EMIRATES = 'United Arab Emirates',
  UNITED_KINGDOM = 'United Kingdom',
  UNITED_STATES = 'United States',
  URUGUAY = 'Uruguay',
  UZBEKISTAN = 'Uzbekistan',
  VANUATU = 'Vanuatu',
  VENEZUELA = 'Venezuela',
  VIETNAM = 'Vietnam',
  VIRGIN_ISLANDS_BRITISH = 'Virgin Islands, British',
  VIRGIN_ISLANDS_US = 'Virgin Islands, U.S.',
  WALLIS_AND_FUTUNA = 'Wallis and Futuna',
  WESTERN_SAHARA = 'Western Sahara',
  YEMEN = 'Yemen',
  ZAMBIA = 'Zambia',
  ZIMBABWE = 'Zimbabwe',
  OTHERS = 'others',
}

export const countryCurrency: Record<Country, Currency> = {
  // AU region countries use AUD
  [Country.AUSTRALIA]: Currency.AUD,
  [Country.BANGLADESH]: Currency.AUD,
  [Country.BHUTAN]: Currency.AUD,
  [Country.BRUNEI_DARUSSALAM]: Currency.AUD,
  [Country.CAMBODIA]: Currency.AUD,
  [Country.CHRISTMAS_ISLAND]: Currency.AUD,
  [Country.COCOS_KEELING_ISLANDS]: Currency.AUD,
  [Country.COOK_ISLANDS]: Currency.AUD,
  [Country.FIJI]: Currency.AUD,
  [Country.FRENCH_SOUTHERN_TERRITORIES]: Currency.AUD,
  [Country.GUAM]: Currency.AUD,
  [Country.INDIA]: Currency.AUD,
  [Country.KIRIBATI]: Currency.AUD,
  [Country.KOREA_DEMOCRATIC_PEOPLES_REPUBLIC]: Currency.AUD,
  [Country.KOREA_REPUBLIC]: Currency.AUD,
  [Country.LAO_PEOPLES_DEMOCRATIC_REPUBLIC]: Currency.AUD,
  [Country.MACAO]: Currency.AUD,
  [Country.MALDIVES]: Currency.AUD,
  [Country.MARSHALL_ISLANDS]: Currency.AUD,
  [Country.MICRONESIA]: Currency.AUD,
  [Country.MONGOLIA]: Currency.AUD,
  [Country.MYANMAR]: Currency.AUD,
  [Country.NAURU]: Currency.AUD,
  [Country.NEPAL]: Currency.AUD,
  [Country.NEW_CALEDONIA]: Currency.AUD,
  [Country.NIUE]: Currency.AUD,
  [Country.NORFOLK_ISLAND]: Currency.AUD,
  [Country.NORTHERN_MARIANA_ISLANDS]: Currency.AUD,
  [Country.PAKISTAN]: Currency.AUD,
  [Country.PALAU]: Currency.AUD,
  [Country.PAPUA_NEW_GUINEA]: Currency.AUD,
  [Country.PHILIPPINES]: Currency.AUD,
  [Country.PITCAIRN]: Currency.AUD,
  [Country.SAMOA]: Currency.AUD,
  [Country.SOLOMON_ISLANDS]: Currency.AUD,
  [Country.SOUTH_GEORGIA_AND_SANDWICH_ISLANDS]: Currency.AUD,
  [Country.SRI_LANKA]: Currency.AUD,
  [Country.TAIWAN]: Currency.AUD,
  [Country.THAILAND]: Currency.AUD,
  [Country.TOKELAU]: Currency.AUD,
  [Country.TONGA]: Currency.AUD,
  [Country.TUVALU]: Currency.AUD,
  [Country.VANUATU]: Currency.AUD,
  [Country.VIETNAM]: Currency.AUD,
  [Country.OTHERS]: Currency.AUD,
  // AU region alternate currency CNY, HKD, IDR, JPY, MYR, NZD, SGD
  [Country.CHINA]: Currency.CNY,
  [Country.HONG_KONG]: Currency.HKD,
  [Country.TIMOR_LESTE]: Currency.IDR,
  [Country.INDONESIA]: Currency.IDR,
  [Country.JAPAN]: Currency.JPY,
  [Country.MALAYSIA]: Currency.MYR,
  [Country.NEW_ZEALAND]: Currency.NZD,
  [Country.SINGAPORE]: Currency.SGD,
  // CA region countries use CAD
  [Country.CANADA]: Currency.CAD,
  // EU region countries use EUR
  [Country.AFGHANISTAN]: Currency.EUR,
  [Country.ALBANIA]: Currency.EUR,
  [Country.ALGERIA]: Currency.EUR,
  [Country.ANDORRA]: Currency.EUR,
  [Country.ANGOLA]: Currency.EUR,
  [Country.ARMENIA]: Currency.EUR,
  [Country.AUSTRIA]: Currency.EUR,
  [Country.AZERBAIJAN]: Currency.EUR,
  [Country.BAHRAIN]: Currency.EUR,
  [Country.BELARUS]: Currency.EUR,
  [Country.BELGIUM]: Currency.EUR,
  [Country.BENIN]: Currency.EUR,
  [Country.BOSNIA_AND_HERZEGOVINA]: Currency.EUR,
  [Country.BOTSWANA]: Currency.EUR,
  [Country.BULGARIA]: Currency.EUR,
  [Country.BURKINA_FASO]: Currency.EUR,
  [Country.BURUNDI]: Currency.EUR,
  [Country.CAMEROON]: Currency.EUR,
  [Country.CAPE_VERDE]: Currency.EUR,
  [Country.CENTRAL_AFRICAN_REPUBLIC]: Currency.EUR,
  [Country.CHAD]: Currency.EUR,
  [Country.COMOROS]: Currency.EUR,
  [Country.CONGO_DEMOCRATIC_REPUBLIC]: Currency.EUR,
  [Country.CONGO_REPUBLIC]: Currency.EUR,
  [Country.COTE_DIVOIRE]: Currency.EUR,
  [Country.CROATIA]: Currency.EUR,
  [Country.CYPRUS]: Currency.EUR,
  [Country.CZECH_REPUBLIC]: Currency.EUR,
  [Country.DENMARK]: Currency.EUR,
  [Country.DJIBOUTI]: Currency.EUR,
  [Country.EGYPT]: Currency.EUR,
  [Country.EQUATORIAL_GUINEA]: Currency.EUR,
  [Country.ERITREA]: Currency.EUR,
  [Country.ESTONIA]: Currency.EUR,
  [Country.ETHIOPIA]: Currency.EUR,
  [Country.FAROE_ISLANDS]: Currency.EUR,
  [Country.FINLAND]: Currency.EUR,
  [Country.FRANCE]: Currency.EUR,
  [Country.GABON]: Currency.EUR,
  [Country.GAMBIA]: Currency.EUR,
  [Country.GEORGIA]: Currency.EUR,
  [Country.GERMANY]: Currency.EUR,
  [Country.GHANA]: Currency.EUR,
  [Country.GIBRALTAR]: Currency.EUR,
  [Country.GREECE]: Currency.EUR,
  [Country.GREENLAND]: Currency.EUR,
  [Country.GUERNSEY]: Currency.EUR,
  [Country.GUINEA]: Currency.EUR,
  [Country.GUINEA_BISSAU]: Currency.EUR,
  [Country.HOLY_SEE]: Currency.EUR,
  [Country.HUNGARY]: Currency.EUR,
  [Country.ICELAND]: Currency.EUR,
  [Country.IRAN]: Currency.EUR,
  [Country.IRAQ]: Currency.EUR,
  [Country.IRELAND]: Currency.EUR,
  [Country.ISLE_OF_MAN]: Currency.EUR,
  [Country.ISRAEL]: Currency.EUR,
  [Country.ITALY]: Currency.EUR,
  [Country.JERSEY]: Currency.EUR,
  [Country.JORDAN]: Currency.EUR,
  [Country.KAZAKHSTAN]: Currency.EUR,
  [Country.KENYA]: Currency.EUR,
  [Country.KUWAIT]: Currency.EUR,
  [Country.KYRGYZSTAN]: Currency.EUR,
  [Country.LATVIA]: Currency.EUR,
  [Country.LEBANON]: Currency.EUR,
  [Country.LIBERIA]: Currency.EUR,
  [Country.LIBYA]: Currency.EUR,
  [Country.LITHUANIA]: Currency.EUR,
  [Country.LUXEMBOURG]: Currency.EUR,
  [Country.MACEDONIA]: Currency.EUR,
  [Country.MADAGASCAR]: Currency.EUR,
  [Country.MALAWI]: Currency.EUR,
  [Country.MALI]: Currency.EUR,
  [Country.MALTA]: Currency.EUR,
  [Country.MAURITANIA]: Currency.EUR,
  [Country.MAURITIUS]: Currency.EUR,
  [Country.MAYOTTE]: Currency.EUR,
  [Country.MOLDOVA]: Currency.EUR,
  [Country.MONACO]: Currency.EUR,
  [Country.MONTENEGRO]: Currency.EUR,
  [Country.MOROCCO]: Currency.EUR,
  [Country.MOZAMBIQUE]: Currency.EUR,
  [Country.NETHERLANDS]: Currency.EUR,
  [Country.NIGER]: Currency.EUR,
  [Country.NIGERIA]: Currency.EUR,
  [Country.NORWAY]: Currency.EUR,
  [Country.OMAN]: Currency.EUR,
  [Country.PALESTINE]: Currency.EUR,
  [Country.POLAND]: Currency.EUR,
  [Country.PORTUGAL]: Currency.EUR,
  [Country.QATAR]: Currency.EUR,
  [Country.REUNION]: Currency.EUR,
  [Country.ROMANIA]: Currency.EUR,
  [Country.RUSSIAN_FEDERATION]: Currency.EUR,
  [Country.RWANDA]: Currency.EUR,
  [Country.SAINT_BARTHELEMY]: Currency.EUR,
  [Country.SAINT_HELENA]: Currency.EUR,
  [Country.SAINT_MARTIN]: Currency.EUR,
  [Country.SAINT_PIERRE_AND_MIQUELON]: Currency.EUR,
  [Country.SAN_MARINO]: Currency.EUR,
  [Country.SAO_TOME_AND_PRINCIPE]: Currency.EUR,
  [Country.SAUDI_ARABIA]: Currency.EUR,
  [Country.SENEGAL]: Currency.EUR,
  [Country.SERBIA]: Currency.EUR,
  [Country.SEYCHELLES]: Currency.EUR,
  [Country.SIERRA_LEONE]: Currency.EUR,
  [Country.SLOVAKIA]: Currency.EUR,
  [Country.SLOVENIA]: Currency.EUR,
  [Country.SOMALIA]: Currency.EUR,
  [Country.SOUTH_SUDAN]: Currency.EUR,
  [Country.SPAIN]: Currency.EUR,
  [Country.SUDAN]: Currency.EUR,
  [Country.SVALBARD_AND_JAN_MAYEN]: Currency.EUR,
  [Country.SWAZILAND]: Currency.EUR,
  [Country.SWEDEN]: Currency.EUR,
  [Country.SYRIAN_ARAB_REPUBLIC]: Currency.EUR,
  [Country.TAJIKISTAN]: Currency.EUR,
  [Country.TANZANIA]: Currency.EUR,
  [Country.TOGO]: Currency.EUR,
  [Country.TUNISIA]: Currency.EUR,
  [Country.TURKEY]: Currency.EUR,
  [Country.TURKMENISTAN]: Currency.EUR,
  [Country.UGANDA]: Currency.EUR,
  [Country.UKRAINE]: Currency.EUR,
  [Country.UNITED_ARAB_EMIRATES]: Currency.EUR,
  [Country.UZBEKISTAN]: Currency.EUR,
  [Country.WESTERN_SAHARA]: Currency.EUR,
  [Country.YEMEN]: Currency.EUR,
  [Country.ZAMBIA]: Currency.EUR,
  [Country.ZIMBABWE]: Currency.EUR,
  // EU region alternative currencies CHF, ZAR
  [Country.SWITZERLAND]: Currency.CHF,
  [Country.LIECHTENSTEIN]: Currency.CHF,
  [Country.SOUTH_AFRICA]: Currency.ZAR,
  [Country.NAMIBIA]: Currency.ZAR,
  [Country.LESOTHO]: Currency.ZAR,
  // UK region countries use GBP
  [Country.BRITISH_INDIAN_OCEAN_TERRITORY]: Currency.GBP,
  [Country.UNITED_KINGDOM]: Currency.GBP,
  [Country.VIRGIN_ISLANDS_BRITISH]: Currency.GBP,
  // US region countries use USD
  [Country.AMERICAN_SAMOA]: Currency.USD,
  [Country.ANGUILLA]: Currency.USD,
  [Country.ANTARCTICA]: Currency.USD,
  [Country.ANTIGUA_AND_BARBUDA]: Currency.USD,
  [Country.ARGENTINA]: Currency.USD,
  [Country.ARUBA]: Currency.USD,
  [Country.BAHAMAS]: Currency.USD,
  [Country.BARBADOS]: Currency.USD,
  [Country.BELIZE]: Currency.USD,
  [Country.BERMUDA]: Currency.USD,
  [Country.BOLIVIA]: Currency.USD,
  [Country.BRAZIL]: Currency.USD,
  [Country.CAYMAN_ISLANDS]: Currency.USD,
  [Country.CHILE]: Currency.USD,
  [Country.COLOMBIA]: Currency.USD,
  [Country.COSTA_RICA]: Currency.USD,
  [Country.CUBA]: Currency.USD,
  [Country.CURACAO]: Currency.USD,
  [Country.DOMINICA]: Currency.USD,
  [Country.DOMINICAN_REPUBLIC]: Currency.USD,
  [Country.ECUADOR]: Currency.USD,
  [Country.EL_SALVADOR]: Currency.USD,
  [Country.FALKLAND_ISLANDS]: Currency.USD,
  [Country.FRENCH_GUIANA]: Currency.USD,
  [Country.GRENADA]: Currency.USD,
  [Country.GUADELOUPE]: Currency.USD,
  [Country.GUATEMALA]: Currency.USD,
  [Country.GUYANA]: Currency.USD,
  [Country.HAITI]: Currency.USD,
  [Country.HONDURAS]: Currency.USD,
  [Country.JAMAICA]: Currency.USD,
  [Country.MARTINIQUE]: Currency.USD,
  [Country.MEXICO]: Currency.USD,
  [Country.MONTSERRAT]: Currency.USD,
  [Country.NICARAGUA]: Currency.USD,
  [Country.PANAMA]: Currency.USD,
  [Country.PARAGUAY]: Currency.USD,
  [Country.PERU]: Currency.USD,
  [Country.PUERTO_RICO]: Currency.USD,
  [Country.SAINT_KITTS_AND_NEVIS]: Currency.USD,
  [Country.SAINT_LUCIA]: Currency.USD,
  [Country.SAINT_VINCENT_AND_GRENADINES]: Currency.USD,
  [Country.SINT_MAARTEN]: Currency.USD,
  [Country.SURINAME]: Currency.USD,
  [Country.TRINIDAD_AND_TOBAGO]: Currency.USD,
  [Country.TURKS_AND_CAICOS_ISLANDS]: Currency.USD,
  [Country.UNITED_STATES]: Currency.USD,
  [Country.URUGUAY]: Currency.USD,
  [Country.VENEZUELA]: Currency.USD,
  [Country.VIRGIN_ISLANDS_US]: Currency.USD,
  [Country.WALLIS_AND_FUTUNA]: Currency.USD,
};
